import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import Spin from 'antd/lib/spin';
import { Space } from 'antd';
import { Pagination } from 'uiKit/atoms/Pagination/Pagination';
import { Row } from 'uiKit/atoms/Row/Row';
import { Col } from 'uiKit/atoms/Col/Col';
import { LoadingOutlined } from '@ant-design/icons';
import { useQueryParams } from '../../hooks/useQueryParams';
import { getLotCategoryData } from '../../helpers/utils';
import Sorting from './components/Sorting/Sorting';
import FilterButton from './components/FilterButton/FilterButton';
import LotsPerPage from './components/LotsPerPage/LotsPerPage';
import CurrencySelector from './components/CurrencySelector/CurrencySelector';
import LotsList from './components/LotsList/LotsList';
import Tag from '../Tag/Tag';
import { AuthContext } from 'context/AuthContext';
import { useStores } from 'stores';
import styles from './Lots.module.scss';
import PropTypes from 'prop-types';

const Lots = props => {
  const { isAuthenticated } = useContext(AuthContext);
  const {
    lotStore: {
      searchInDescription,
      updateCurrentPage,
      changeSearchURL,
      setFilters,
      lotData,
      query,
      lotsPerPage,
      currentPage,
    },
    lotStore,
    userStore,
    singleLotStore,
    spinnerStore,
  } = useStores();

  const { isLotDataLoading, lotTotal, title, onFilterChange, hideFiltersCallback, hideFilters } = props;
  const { filters } = lotStore;

  const { query: searchQuery, locationIds, categoryId, productType, lotState } = filters;

  const { locations } = userStore;

  const { wipeSingleLot, lotCategories, tags } = singleLotStore;

  const controlPanel = useRef(null);
  const { isSpinning } = spinnerStore;
  const location = useLocation();
  const { queryParams, updateQueryParams } = useQueryParams();
  const [tag, setTag] = useState({});
  const [categoryName, setCategoryName] = useState('');
  const lotsAnchorRef = useRef(null);

  const tagIds = queryParams?.tagIds;

  const setTagHandler = useCallback(() => {
    const tagName = tags.find(obj => obj.id === parseInt(tagIds, 10))?.name;
    setTag({ id: tagIds, name: tagName });
  }, [tagIds, tags]);

  const handleDeleteTag = event => {
    const id = event.target.id;
    id === 'clear' &&
      onFilterChange([
        { name: 'productType', value: '' },
        { name: 'lotState', value: null },
        { name: 'categoryId', value: '' },
        { name: 'query', value: '' },
        { name: 'tagIds', value: [] },
        { name: 'locationIds', value: [] },
      ]);
    id === 'productType' && onFilterChange([{ name: 'productType', value: '' }]);
    id === 'productType&lotState' &&
      onFilterChange([
        { name: 'productType', value: '' },
        { name: 'lotState', value: null },
      ]);
    id === 'categoryId' && onFilterChange([{ name: 'categoryId', value: '' }]);
    id === 'query' && onFilterChange([{ name: 'query', value: '' }]);
    id === 'tag' && onFilterChange([{ name: 'tagIds', value: [] }]);
    id.includes('locationIds') &&
      (locationIds.length === 1
        ? onFilterChange([{ name: 'locationIds', value: [] }])
        : locationIds.length > 1 &&
          onFilterChange([{ name: 'locationIds', value: locationIds.filter(item => item !== id.split('-')[1]) }]));
  };

  useEffect(() => {
    setTag(tag, queryParams.tagIds);
  }, [queryParams, tag, tagIds]);

  const getCurrentCategoryName = useCallback(() => {
    const currentCategoryId = Number(queryParams.categoryId) || 0;

    if (currentCategoryId === 0) {
      setCategoryName(title);
      return;
    }

    const { category, subcategory } = getLotCategoryData(lotCategories, currentCategoryId);

    if (subcategory) {
      setCategoryName(`${category.name} · ${subcategory.name}`);
    } else {
      setCategoryName(category?.name);
    }
  }, [lotCategories, queryParams.categoryId, title]);

  const changePage = (event, pageNumber) => {
    updateCurrentPage(pageNumber);
    changeSearchURL(location);
    setFilters({
      pageNumber,
    });
    updateQueryParams([{ name: 'pageNumber', value: pageNumber }]);

    window.scrollTo({
      top: lotsAnchorRef.current?.offsetTop,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    wipeSingleLot();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setTagHandler();
  }, [tags, setTagHandler]);

  useEffect(() => {
    getCurrentCategoryName();
  }, [lotCategories, queryParams, getCurrentCategoryName]);

  const renderEmptyLotList = () => {
    let displayMessage = 'Oops... There are no lots found!';

    if (location.pathname === '/favorites') {
      displayMessage = "You don't have favorite lots yet";
    } else if (location.pathname === '/mylots' && isAuthenticated) {
      displayMessage = "You haven't added any lots yet";
    } else if (searchInDescription && query !== '') {
      displayMessage = (
        <>
          <b>&quot;{query}&quot;</b> - No Lots found
        </>
      );
    }

    return (
      <div className={styles.bns3_error_message_container}>
        <h1 className={styles.bns3_error_message}>{displayMessage}</h1>
      </div>
    );
  };

  const renderLotList = () => (
    <>
      <Row className={styles.bns3_lots_list_container}>
        <LotsList />
      </Row>
      <Row justifyContent="flex-end">
        <Pagination
            size='large'
            shape="rounded"
            siblingCount={0}
            count={Math.ceil(lotTotal / (Number(queryParams.itemsPerPage) || lotsPerPage))}
            page={currentPage}
            onChange={changePage}
          />
      </Row>
    </>
  );

  const renderLocations = () => {
    return locations
      .filter(item => locationIds.includes(item.id.toString()))
      .map(item => (
        <Tag name={item.name} id={`locationIds-${item.id}`} key={item.id} handleDeleteTag={handleDeleteTag} />
      ));
  };

  const renderCategory = () => {
    if (categoryId !== '' && categoryId !== 0) {
      return <Tag name={categoryName} id="categoryId" key={categoryId} handleDeleteTag={handleDeleteTag} />;
    }
  };

  const renderProductType = () => {
    let updatedProductType = productType.charAt(0).toUpperCase() + productType.slice(1);
    if (lotState) {
      let updatedLotState = lotState.charAt(0).toUpperCase() + lotState.slice(1);
      return (
        <Tag
          name={`${updatedProductType}, ${updatedLotState}`}
          id="productType&lotState"
          handleDeleteTag={handleDeleteTag}
        />
      );
    } else {
      return <Tag name={updatedProductType} id="productType" handleDeleteTag={handleDeleteTag} />;
    }
  };

  useEffect(() => {
    const el = controlPanel.current;
    if (el) {
      if (hideFilters) {
        el.style.top = 60 + 'px';
      }
      if (!hideFilters) {
        el.style.top = 339 + 'px';
      }
    }
  }, [controlPanel, hideFilters]);

  return (
    <div ref={lotsAnchorRef}>
      <div className={styles.bns3_lots_control_panel_container_wrapper} ref={controlPanel}>
        <div className={styles.bns3_lots_control_panel_container}>
          <div className={styles.bns3_lots_control_panel_section}>
            <Col>
              <Space align="start" direction="horizontal" size="small" className={styles.bns3_lots_title_container}>
                <h2 className={styles.bns3_lots_title}>{categoryName}</h2>
                <span className={styles.bns3_lots_total_amount}>{lotTotal}</span>
              </Space>
            </Col>
            <Col>
              <Space align="start" direction="horizontal" size="small" className={styles.bns3_lots_title_container}>
                <FilterButton hideFiltersCallback={hideFiltersCallback} hideFilters={hideFilters} />
              </Space>
            </Col>
          </div>
          <Col className={styles.bns3_lots_list_parameters}>
            <Sorting onFilterChange={onFilterChange} />
            <CurrencySelector onFilterChange={onFilterChange} />
            <LotsPerPage onFilterChange={onFilterChange} />
          </Col>
        </div>
        {(searchQuery || locationIds.length || categoryId || productType || tag.name) && (
          <div className={styles.bns3_lots_filter_criteria_container}>
            {searchQuery && <Tag name={searchQuery} id="query" handleDeleteTag={handleDeleteTag} />}
            {renderLocations()}
            {renderCategory()}
            {productType && renderProductType()}
            {tag.name && <Tag name={tag.name} id="tag" handleDeleteTag={handleDeleteTag} />}
            <button className={styles.bns3_lots_filter_clear} type="button" id="clear" onClick={handleDeleteTag}>
              Clear All
            </button>
          </div>
        )}
      </div>
      <Space direction="vertical" className={styles.bns3_lots} size="middle">
        <div className={styles.bns3_mainContainer}>
          {isLotDataLoading ? (
            <div className={styles.spinner_container}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : (
            <>
              {lotData.length ? renderLotList() : null}
              {!isSpinning && !lotData.length ? renderEmptyLotList() : null}
            </>
          )}
        </div>
      </Space>
    </div>
  );
};

Lots.propTypes = {
  isLotDataLoading: PropTypes.bool,
  lotTotal: PropTypes.number,
  title: PropTypes.string,
  onFilterChange: PropTypes.func,
  hideFiltersCallback: PropTypes.func,
};

export default observer(Lots);
